// Reloading the page when IndexedDB cannot be reopened (GitHub issue #3906).
//
// iOS kills WebKit's Networking process, which hosts the IndexedDB server, while the app sits in
// the background. On WebKit before iOS 26.5 the page then keeps a proxy to the dead connection, so
// every later indexedDB.open() from it fails with "Connection to Indexed Database server lost" until
// a navigation creates a new document (WebKit bug 309386). Retrying does not help - the flag behind
// that error is never reset - so the only recovery is the one the message asks for: a reload.
//
// The reload goes through the update gate like a new bundle does (app_update.ts): at once unless a
// song is playing or was only just paused, or the user is in the middle of something. A marker in
// localStorage, which survives the reload (the same crash that kills IndexedDB leaves localStorage
// working), guards against a loop: at most one reload per reloadCoolDownMs, and the report of a
// failure inside that window says whether the reload had helped (an operation succeeded on the
// new page) or not. After the window a fresh failure is reloaded for again, so a stale marker
// never pins a page to its dead storage.
//
// idb.ts decides and schedules in two steps - decideDeadStorageReload(), then
// scheduleDeadStorageReload() - so that the report it files about the failure can say what was
// decided before the reload, which can fire at once, takes the page away.

import { localStorageGet, LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { runWhenUpdateAllowed } from '../player/update_gate.ts';
import { describeAppReloadTiming } from './app_update.ts';

export type DeadStorageReloadDecision =
  | { kind: 'scheduled'; timing: string } // timing: when the gate will let it reload, as a log phrase
  | { kind: 'already scheduled' }
  | { kind: 'skipped'; reason: string };

export const reloadCoolDownMs = 10 * 60_000;
// how long the reload waits for the report about the failure to be sent before going ahead anyway
const reportWaitMs = 2000;
// distinct from the 'app-reload' and 'song-files' keys, so neither evicts this one
const storageReloadUpdateKey = 'storage-reload';

let reloadScheduled = false;
let recoveryChecked = false;
let reloadPage: () => void = () => window.location.reload();

export function decideDeadStorageReload(now = Date.now()): DeadStorageReloadDecision {
  if (reloadScheduled) {
    return { kind: 'already scheduled' };
  }
  const previous = localStorageGet(LocalStorageKey.DeadStorageReload);
  if (previous && now - previous.reloadedAt < reloadCoolDownMs) {
    const secondsAgo = Math.round((now - previous.reloadedAt) / 1000);
    return {
      kind: 'skipped',
      reason:
        previous.recoveredAt === undefined
          ? `the page already reloaded for this ${secondsAgo} s ago and it did not help`
          : `the page reloaded for this ${secondsAgo} s ago, so it is not reloaded again yet`,
    };
  }
  return { kind: 'scheduled', timing: describeAppReloadTiming(now) };
}

// Reloads once the gate allows. `fileReport` is called first and given reportWaitMs to finish, so the
// reload does not cancel the request that reports the failure.
export function scheduleDeadStorageReload(fileReport: () => Promise<void>): void {
  reloadScheduled = true;
  runWhenUpdateAllowed(
    () => {
      void reloadNow(fileReport);
    },
    // exclusive: the page is going away; waitForUserIdle: a reload loses an uncommitted planner cell
    { key: storageReloadUpdateKey, exclusive: true, waitForUserIdle: true },
  );
}

async function reloadNow(fileReport: () => Promise<void>): Promise<void> {
  localStorageSet(LocalStorageKey.DeadStorageReload, { reloadedAt: Date.now() });
  await Promise.race([fileReport(), new Promise((resolve) => setTimeout(resolve, reportWaitMs))]);
  console.log('Reloading the page for its dead IndexedDB connection (GitHub issue #3906)');
  reloadPage();
}

// Called when an operation succeeds. The first success of a page load that followed a dead-storage
// reload is the evidence that the reload helped: it is recorded in the marker (which also ends the
// loop guard's "did not help" verdict) and described for a report. Every later call returns nothing.
export function noteStorageWorking(now = Date.now()): string | undefined {
  if (recoveryChecked) {
    return undefined;
  }
  recoveryChecked = true;
  const previous = localStorageGet(LocalStorageKey.DeadStorageReload);
  if (!previous || previous.recoveredAt !== undefined) {
    return undefined;
  }
  localStorageSet(LocalStorageKey.DeadStorageReload, { ...previous, recoveredAt: now });
  return `${Math.round((now - previous.reloadedAt) / 1000)} s after the page reloaded for its dead connection`;
}

// test-only: the flags are module state, and jsdom cannot reload a page
export function resetStorageReloadForTests(reloadPageOverride?: () => void): void {
  reloadScheduled = false;
  recoveryChecked = false;
  reloadPage = reloadPageOverride ?? (() => window.location.reload());
}
